import { __decorate, __metadata } from "tslib";
// NPM
import { Component, Mixins, Prop } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
// S1
import BaseMixer from "@/router/views/BaseMixer";
import { strictDateFormatter } from "@/utilities/strictDateFormatter";
import { toaster } from "@/utilities/toaster";
import { daysAgo, isAfterToday, isBeforeDay, monthsAgo, parseDate, sameId } from "../updatesHelpers";
const addDays = (value, days) => {
    const date = new Date(value.getTime());
    date.setDate(date.getDate() + days);
    return date;
};
// Longest range the Custom period may span
const MAX_CUSTOM_RANGE_DAYS = 180;
/**
 * Updates screen filter sidebar: client picker, the client's teams, period pills + date range, and Clear Filter.
 * Owns the team / period / range pickers and the collapsed state; the page owns the chosen client.
 * Emits change-client, change-team and change-date-range.
 */
let UpdatesFilter = /** @class */ (() => {
    var _a, _b, _c;
    let UpdatesFilter = class UpdatesFilter extends Mixins(BaseMixer) {
        constructor() {
            super(...arguments);
            this.strictDateFormatter = strictDateFormatter;
            this.periods = [
                { key: "30d", label: "30 Days" },
                { key: "3m", label: "3 Months" },
                { key: "custom", label: "Custom" },
            ];
            this.dateRange = [this.dateFrom, this.dateTo];
            this.period = "3m";
            this.collapsed = false;
        }
        // Client picker: active and inactive clients under their own headers (an empty group isn't shown)
        get clientGroups() {
            const clients = this.clientOptions || [];
            return [
                { label: "Active", clients: clients.filter((c) => c.isActive !== false) },
                { label: "Inactive", clients: clients.filter((c) => c.isActive === false) },
            ].filter((group) => group.clients.length > 0);
        }
        get selectedClient() {
            return (this.clientOptions || []).find((c) => sameId(c.id, this.clientId)) || null;
        }
        set selectedClient(client) {
            const id = client ? client.id.toString() : "";
            if (!id || sameId(id, this.clientId)) {
                return;
            }
            this.$emit("change-client", id);
        }
        // Team picker: same as the client picker - active and inactive teams under their own headers
        get teamGroups() {
            const teams = this.teamOptions || [];
            return [
                { label: "Active", teams: teams.filter((t) => t.isActive !== false) },
                { label: "Inactive", teams: teams.filter((t) => t.isActive === false) },
            ].filter((group) => group.teams.length > 0);
        }
        // null until the client's teams have loaded
        get selectedTeam() {
            return (this.teamOptions || []).find((t) => sameId(t.id, this.teamId)) || null;
        }
        set selectedTeam(team) {
            const id = team ? team.id.toString() : "";
            if (!id || sameId(id, this.teamId)) {
                return;
            }
            this.$emit("change-team", id);
        }
        toggleCollapsed() {
            this.collapsed = !this.collapsed;
            // Timeline truncation is measured from width; remeasure after the sidebar finishes moving
            window.setTimeout(() => window.dispatchEvent(new Event("resize")), 260);
        }
        // Back to the page defaults: the client and team it opened on, and the 3-month period
        clearFilters() {
            const nextClient = this.defaultClientId || this.clientId;
            const nextTeam = this.defaultTeamId || null;
            const clientChanged = !!nextClient && !sameId(nextClient, this.clientId);
            if (clientChanged) {
                // A new client starts with no team, so the default team is set again after it
                this.$emit("change-client", nextClient);
            }
            const teamUnchanged = (!this.teamId && !nextTeam) || sameId(this.teamId, nextTeam);
            if (clientChanged ? !!nextTeam : !teamUnchanged) {
                this.$emit("change-team", nextTeam);
            }
            this.period = "3m";
            this.dateRange = [monthsAgo(3), new Date()];
            this.applyDateRange();
        }
        selectPeriod(key) {
            this.period = key;
            if (key === "custom") {
                return;
            }
            const from = key === "30d" ? daysAgo(30) : monthsAgo(3);
            this.dateRange = [from, new Date()];
            this.applyDateRange();
        }
        // Custom range: no future days and nothing before the client's Start Date; once the start is clicked,
        // the end must fall within 180 days after it
        disableRangeDate(date, currentValue) {
            const start = parseDate((currentValue || [])[0]);
            const pickingEnd = !!start && !parseDate((currentValue || [])[1]);
            return (isAfterToday(date) ||
                isBeforeDay(date, this.clientStartDate) ||
                (pickingEnd && (date < start || date > addDays(start, MAX_CUSTOM_RANGE_DAYS))));
        }
        onDateRangeChange() {
            this.period = "custom";
            this.applyDateRange();
        }
        // A typed range the strict parser rejected (impossible date or outside the allowed days) - the filter keeps its old range
        onDateRangeInputError(text) {
            toaster.ShowError(`“${text}” isn't a valid date range. Use ${this.dateFormat} for both dates.`);
        }
        applyDateRange() {
            const from = parseDate((this.dateRange || [])[0]);
            const to = parseDate((this.dateRange || [])[1]);
            if (from && to && from <= to) {
                this.$emit("change-date-range", { dateFrom: from, dateTo: to });
            }
        }
    };
    __decorate([
        Prop({ default: () => [] }),
        __metadata("design:type", Array)
    ], UpdatesFilter.prototype, "clientOptions", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], UpdatesFilter.prototype, "clientId", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], UpdatesFilter.prototype, "defaultClientId", void 0);
    __decorate([
        Prop({ default: () => [] }),
        __metadata("design:type", Array)
    ], UpdatesFilter.prototype, "teamOptions", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], UpdatesFilter.prototype, "teamId", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], UpdatesFilter.prototype, "defaultTeamId", void 0);
    __decorate([
        Prop({ default: null }),
        __metadata("design:type", typeof (_a = typeof Date !== "undefined" && Date) === "function" ? _a : Object)
    ], UpdatesFilter.prototype, "clientStartDate", void 0);
    __decorate([
        Prop({ required: true }),
        __metadata("design:type", typeof (_b = typeof Date !== "undefined" && Date) === "function" ? _b : Object)
    ], UpdatesFilter.prototype, "dateFrom", void 0);
    __decorate([
        Prop({ required: true }),
        __metadata("design:type", typeof (_c = typeof Date !== "undefined" && Date) === "function" ? _c : Object)
    ], UpdatesFilter.prototype, "dateTo", void 0);
    UpdatesFilter = __decorate([
        Component({
            name: "UpdatesFilter",
            components: {
                Multiselect,
            },
        })
    ], UpdatesFilter);
    return UpdatesFilter;
})();
export default UpdatesFilter;
